import {useTranslation} from 'react-i18next';
import {FormLabel, Grid} from '@mui/material';
import MessageBox from '../../../../util/messageBox';
import React, {useEffect, useRef, useState} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import _ from 'lodash';
import moment from 'moment';
import config from '../../../../app/config';
import API from '../../../../app/api';
import {useDispatch} from 'react-redux';
import {addNoteForPatient} from '../../../../store/reducers/notes';
import Notes from '../../../../util/notes';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import useOpenState from '../../../../hooks/useOpenState';
import {setSnackbar} from '../../../../store/reducers/globals';
import InformationBox from '../../../../components/other/informationBox';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import AgyleDialog from '../../../../components/modal/agyleDialog';
import {hasPermission, Permissions} from '../../../../store/reducers/user';

const useStyles = makeStyles((theme) => ({
    button: {
        background: '#fff',
        color: '#000',
        '&:hover': {
            background: '#fff',
        },
    },
    info: {
        fontStyle: 'italic',
        fontWeight: 'bold',
        '&.center': {
            textAlign: 'center',
        },
    },
    error: {
        color: '#f44336',
    },
    input: {
        width: '100%',
    },
    picker: {
        margin: '5px 0',
    },
    actionBox: {
        padding: 5,
    },
    label: {
        color: theme.palette.mode === 'light' ? '#000' : '#FFF',
        fontWeight: 'bold',
        margin: '10px 0',
    },
}));

const ObsTestEdit = (props) => {
    const classesLocal = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    // Message Box Opener
    const [isInformationOpen, openInformation, closeInformation] = useOpenState(false, true);

    // Data
    const [obs, setObs] = useState(null);
    const [dataForm, setDataForm] = useState({touch: false});

    // Error Pickers
    const errorPicker = useRef(null);

    useEffect(() => {
        const obs = _.find(props.observations, {__id: props.selectedObs});
        setObs(obs);
        let error = {};
        let data = {};

        _.forEach(obs?.tests, element => {
            data = {
                ...data,
                [element.name]: element.observationDate,
            };
            error = {
                ...error,
                [element.name]: null,
            };
        });

        setDataForm(prevState => ({
            ...prevState,
            ...data,
        }));
        errorPicker.current = {...error};
    }, []);

    const updateNewTimestamp = (tests) => {
        API.updateObservationTests(props.patient?.patient?.__id, obs.__id, tests)
            .then(r => {
                dispatch(addNoteForPatient(props.patient?.patient.__id, {
                    note: Notes.getObservationEditNote(obs, tests),
                    noteType: 'obs',
                    patientsInWardsRef: [props.patient.__id],
                    episodeId: props.patient?.admission?.consultantEpisodeId || null,
                }, props.patient.__id));
                dispatch(setSnackbar({type: 'success', message: 'Observation tests updated.'}));
                props.refreshObs();
                props.onClose();
            })
            .catch(error => {
                console.error('updateObservationTests Error!');
                dispatch(setSnackbar({type: 'error', message: 'Observation tests update failed.'}));
            });
    };

    // Validation Pickers
    const valid = () => {
        const error = Object.keys(errorPicker.current).find(error => errorPicker.current[error] !== null);
        const empty = Object.keys(dataForm).find(error => dataForm[error] === null);
        return !empty && !error;
    };

    const saveEntry = () => {
        closeInformation();
        if (valid()) {
            let testClone = _.cloneDeep(obs.tests);
            _.forEach(testClone, (test, index) => {
                let orgTest = test;
                orgTest.observationDate = moment(dataForm[test.name]).toISOString();
                testClone[index] = orgTest;
            });
            updateNewTimestamp(testClone);
        }
    };

    const handleChange = (name, event) => {
        setDataForm(prevState => ({
            ...prevState,
            touch: true,
            [name]: event,
        }));
    };

    const renderFields = () => {
        if (dataForm && !_.isEmpty(obs?.tests)) {
            return (
                obs.tests.map(test => (
                    <div key={test.name} className={classesLocal.actionBox}>
                        <Grid container direction="row" justifyContent="space-between" alignItems="center">
                            <Grid item xs={12} md={3}>
                                <FormLabel className={classesLocal.label}>
                                    {_.startCase(test.name)}
                                </FormLabel>
                            </Grid>

                            <Grid item xs={12} md={9} className={classesLocal.picker}>
                                <AgyleDatePicker
                                    name={test?.name}
                                    label={t('Time of examination')}
                                    type="dateTime"
                                    value={dataForm[test.name] || null}
                                    onChange={event => handleChange(test.name, event)}
                                    onError={event => errorPicker.current = {
                                        ...errorPicker.current,
                                        [test.name]: event,
                                    }}
                                />
                            </Grid>
                        </Grid>

                    </div>
                ))
            );
        }
    };

    return (
        <AgyleDialog
            open={props.open}
            title={t('Update Observation Timestamp')}
            width="md"
            fullWidth
            onClose={props.onClose}
            onSave={openInformation}
            disableSubmit={!dataForm.touch || !dispatch(hasPermission(Permissions.AGYLE_IS_ECDS_ADMIN))}
        >
            <>
                {obs &&
                    <>
                        <InformationBox type="info">
                            {t(`Observation completed at ${moment(obs?.examinationStart).format(config.defaultDateTimeFormat)}`)}
                            {t(` by ${obs?.completedByName}`)}
                            {t(` status: ${_.startCase(obs?.status)}`)}
                        </InformationBox>

                        <Grid item xs={12} style={{marginTop: 10}} className={classesLocal.body}>
                            {renderFields()}
                        </Grid>
                    </>
                }

                <MessageBox
                    title={t('Confirmation')}
                    open={isInformationOpen}
                    haveAccept={{yes: t('Submit'), no: t('Close')}}
                    onAccept={saveEntry}
                    onClose={closeInformation}
                    message={t(
                        'Are you sure you want to save new timestamp?',
                    )}
                />
            </>
        </AgyleDialog>
    );
};

export default ObsTestEdit;